import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { style } from 'typestyle';
import * as styles from './display-screen.styles';
import Gauge from '../components/gauge.component';
import { getDisplayViewConfig, getDisplayViewData, pollPairing, startPairing } from './screen.service';
import { type DashboardOfficeDetail, type DisplayViewConfig, type DisplayViewKey } from '../models/dashboard.models';
import i18n from '../../../../i18n';
import RouteConstants from '../../../../core/routes.constants';
import burgerIcon from '../../../../assets/images/burger-icon.svg';

const PAIRING_POLL_INTERVAL_MS = 3000;
const DATA_POLL_INTERVAL_MS = 30000;
const LANGUAGE_ENFORCE_INTERVAL_MS = 5000;

// The kiosk shares the browser-global i18next instance (and its 'whyatwork_language' localStorage
// key) with the authenticated portal. If an admin toggles language in another tab on the same
// browser/profile, and the kiosk browser later reloads the page (common for unattended kiosk
// software — periodic self-refresh, crash recovery), it picks up whatever the admin last chose
// instead of its own fixed language. The screen has no language switcher of its own, so it must not
// depend on that shared, externally-mutable preference at all — it pins a language and re-asserts
// it continuously, independent of localStorage/admin toggles/reloads.
//
// The language comes from the link itself (?lang=en), which is what lets one display be shared as
// both a Dutch and an English URL without needing two display rows. Anything missing or unrecognised
// falls back to Dutch, so bare /scherm/{id} links and every already-paired kiosk keep their existing
// behaviour unchanged.
const SUPPORTED_SCREEN_LANGUAGES = ['nl', 'en'];
const DEFAULT_SCREEN_LANGUAGE = 'nl';

const getScreenLanguage = (): string => {
  const requested = new URLSearchParams(window.location.search).get('lang')?.toLowerCase();
  return requested && SUPPORTED_SCREEN_LANGUAGES.includes(requested) ? requested : DEFAULT_SCREEN_LANGUAGE;
};

const KIOSK_LANGUAGE = getScreenLanguage();

// Each display gets its own /scherm/{displayId} URL and its own localStorage key, so pairing
// multiple displays in the same browser (e.g. previewing several offices from one machine before
// deploying to separate kiosks) doesn't have one pairing overwrite another's token. The bare
// /scherm route (no id segment) falls back to a single shared legacy key for anyone who already
// bookmarked it.
const getDisplayIdFromPath = (): string | undefined => {
  const segments = window.location.pathname.replace(/^\/+|\/+$/g, '').split('/');
  return segments.length > 1 && segments[0] === RouteConstants.PATH_DISPLAY_SCREEN && segments[1]
    ? segments[1]
    : undefined;
};

const displayId = getDisplayIdFromPath();
const TOKEN_STORAGE_KEY = displayId ? `waw_display_token_${displayId}` : 'waw_display_token';

type ScreenState =
  | { phase: 'pairing'; code?: string; pollToken?: string; expired?: boolean }
  | { phase: 'live'; config: DisplayViewConfig; data?: DashboardOfficeDetail }
  | { phase: 'paused'; config: DisplayViewConfig }
  | { phase: 'loading' }
  | { phase: 'error'; message: string };

// The kiosk/TV-side screen. Unauthenticated: identifies itself with a long-lived X-Display-Token
// obtained via the pairing handshake (screen shows a code, an admin enters it in the portal).
const DisplayScreen = (): React.JSX.Element => {
  const { t } = useTranslation();
  const [state, setState] = useState<ScreenState>({ phase: 'loading' });
  const [activeView, setActiveView] = useState<DisplayViewKey | undefined>(undefined);
  const [isPublicView, setIsPublicView] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const enforceLanguage = (): void => {
      if (i18n.language !== KIOSK_LANGUAGE) {
        i18n.changeLanguage(KIOSK_LANGUAGE);
      }
    };

    enforceLanguage();
    const interval = setInterval(enforceLanguage, LANGUAGE_ENFORCE_INTERVAL_MS);
    document.addEventListener('visibilitychange', enforceLanguage);
    window.addEventListener('focus', enforceLanguage);

    return (): void => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', enforceLanguage);
      window.removeEventListener('focus', enforceLanguage);
    };
  }, []);

  const clearToken = useCallback((): void => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }, []);

  // Pairing: request a code, poll until an admin claims it in the portal.
  const beginPairing = useCallback(async (): Promise<void> => {
    const session = await startPairing();
    if (!session) {
      setState({ phase: 'error', message: t('dashboard-display-screen-retry-message') });
      setTimeout(beginPairing, PAIRING_POLL_INTERVAL_MS);
      return;
    }

    setState({ phase: 'pairing', code: session.pairingCode, pollToken: session.pollToken });
  }, [t]);

  // Two ways in. If this browser already paired, use its token. Otherwise the link may be a
  // publicly shared one — try reading it anonymously by display id first, and only fall back to
  // showing a pairing code if the backend says that display isn't shared. Doing it in this order
  // is what makes a shared link work for an ordinary visitor, who has no way to complete pairing.
  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) {
      return undefined;
    }

    if (!displayId) {
      beginPairing();
      return undefined;
    }

    let cancelled = false;
    (async (): Promise<void> => {
      const publicConfig = await getDisplayViewConfig(undefined, displayId);
      if (cancelled) {
        return;
      }

      if (publicConfig.config) {
        setIsPublicView(true);
        setState({ phase: 'loading' });
        return;
      }

      beginPairing();
    })();

    return (): void => {
      cancelled = true;
    };
  }, [beginPairing]);

  // Poll for pairing completion while a code is showing.
  useEffect(() => {
    if (state.phase !== 'pairing' || !state.pollToken) {
      return undefined;
    }

    const { pollToken } = state;
    const interval = setInterval(async () => {
      const result = await pollPairing(pollToken);
      if (!result) {
        return;
      }

      if (result.status === 'paired' && result.displayToken) {
        localStorage.setItem(TOKEN_STORAGE_KEY, result.displayToken);
        setState({ phase: 'loading' });
      } else if (result.status === 'expired') {
        clearInterval(interval);
        beginPairing();
      }
    }, PAIRING_POLL_INTERVAL_MS);

    return (): void => clearInterval(interval);
  }, [state, beginPairing]);

  // A visitor who opened a shared link while sharing happened to be off lands on the pairing code,
  // which they can do nothing with. Keep re-checking so the screen recovers on its own the moment
  // an admin turns sharing back on, instead of stranding an unattended display until someone
  // reloads it by hand.
  useEffect(() => {
    if (state.phase !== 'pairing' || !displayId) {
      return undefined;
    }

    const interval = setInterval(async () => {
      const publicConfig = await getDisplayViewConfig(undefined, displayId);
      if (publicConfig.config) {
        setIsPublicView(true);
        setState({ phase: 'loading' });
      }
    }, DATA_POLL_INTERVAL_MS);

    return (): void => clearInterval(interval);
  }, [state.phase]);

  // Fetch config + data, then keep polling for fresh data and re-checking status. Works for both
  // a paired screen (token) and a shared public link (display id, no token).
  const refresh = useCallback(async (): Promise<void> => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? undefined;
    if (!token && !isPublicView) {
      return;
    }

    const configResult = await getDisplayViewConfig(token, displayId);
    if (configResult.status === 401) {
      // A revoked display, or one whose public link was just switched off, lands here. Either way
      // this viewer has lost access: drop any stored token and fall back to the pairing handshake.
      clearToken();
      setIsPublicView(false);
      beginPairing();
      return;
    }

    if (!configResult.config) {
      // Transient (network blip, backend restart) — retry on the same cadence as the live/paused
      // poll instead of leaving the kiosk stuck on an error screen forever with no self-healing.
      setState({ phase: 'error', message: t('dashboard-display-screen-config-error') });
      setTimeout(refresh, DATA_POLL_INTERVAL_MS);
      return;
    }

    if (configResult.config.status === 'paused') {
      setState({ phase: 'paused', config: configResult.config });
      return;
    }

    const dataResult = await getDisplayViewData(token, displayId);
    if (dataResult.status === 401) {
      clearToken();
      setIsPublicView(false);
      beginPairing();
      return;
    }

    // A failed data call (500, network blip returning status -1, a 423 because the display was paused
    // between the config and data calls) must not wipe the content area: every render block is
    // guarded on `data`, so committing `data: undefined` would blank an unattended wall display for
    // up to DATA_POLL_INTERVAL_MS. Keep whatever is already on screen until a poll succeeds.
    const { config } = configResult;
    setState((previous) => ({
      phase: 'live',
      config,
      data: dataResult.data?.data ?? (previous.phase === 'live' ? previous.data : undefined),
    }));
  }, [beginPairing, clearToken, isPublicView, t]);

  useEffect(() => {
    if (state.phase !== 'loading') {
      return undefined;
    }

    refresh();
    return undefined;
  }, [state.phase, refresh]);

  useEffect(() => {
    if (state.phase !== 'live' && state.phase !== 'paused') {
      return undefined;
    }

    const interval = setInterval(refresh, DATA_POLL_INTERVAL_MS);

    // Re-validate immediately on wake instead of waiting up to DATA_POLL_INTERVAL_MS, so a
    // revoked display loses access promptly rather than staying on stale live/paused content
    // (e.g. a kiosk display waking from screen-off, or a browser tab regaining focus).
    const revalidate = (): void => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('focus', revalidate);

    return (): void => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('focus', revalidate);
    };
  }, [state.phase, refresh]);

  // The rotation effect must depend on the individual config values it reads, never on `state` (or on
  // `state.config`) as a whole: the DATA_POLL_INTERVAL_MS poll calls setState with a freshly built
  // object every 30s, so any rotation interval longer than that would be torn down and restarted
  // before its callback could ever fire, freezing the screen on its first view.
  const liveConfig = state.phase === 'live' ? state.config : undefined;
  const rotationMode = liveConfig?.mode;
  const rotationIntervalSeconds = liveConfig?.rotationIntervalSeconds;
  const configDefaultView = liveConfig?.defaultView;
  // The views array is a new identity on every poll too, so key the effect on its contents.
  const viewsKey = (liveConfig?.views ?? []).join(',');

  // activeView is read but deliberately not a dependency — rotating a view must not restart the timer.
  const activeViewRef = useRef(activeView);
  useEffect(() => {
    activeViewRef.current = activeView;
  }, [activeView]);

  // Auto-rotate: cycle the active view; Touch-select: idle timeout back to the default view.
  useEffect(() => {
    if (state.phase !== 'live') {
      return undefined;
    }

    const views = viewsKey ? (viewsKey.split(',') as DisplayViewKey[]) : [];
    if (views.length === 0) {
      return undefined;
    }

    if (rotationMode === 0) {
      let index = views.findIndex((view) => view === activeViewRef.current);
      if (index < 0) {
        index = 0;
        setActiveView(views[0]);
      }

      const interval = setInterval(() => {
        index = (index + 1) % views.length;
        activeViewRef.current = views[index];
        setActiveView(views[index]);
      }, (rotationIntervalSeconds ?? 12) * 1000);

      return (): void => clearInterval(interval);
    }

    if (!activeViewRef.current) {
      setActiveView((configDefaultView as DisplayViewKey) ?? views[0]);
    }
    return undefined;
  }, [state.phase, rotationMode, rotationIntervalSeconds, configDefaultView, viewsKey]);

  const registerInteraction = useCallback((): void => {
    if (state.phase !== 'live' || state.config.mode !== 1) {
      return;
    }

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      setActiveView((state.config.defaultView as DisplayViewKey) ?? state.config.views?.[0]);
    }, (state.config.idleTimeoutSeconds ?? 60) * 1000);
  }, [state]);

  if (state.phase === 'loading') {
    return <div className={style(styles.screen)}>{t('dashboard-display-screen-loading')}</div>;
  }

  if (state.phase === 'error') {
    return (
      <div className={style(styles.screen)}>
        <div className={style(styles.errorMessage)}>{state.message}</div>
      </div>
    );
  }

  if (state.phase === 'pairing') {
    return (
      <div className={style(styles.screen)}>
        <div className={style(styles.pairingLabel)}>{t('dashboard-display-screen-pairing-instructions')}</div>
        <div className={style(styles.pairingCode)}>{state.code ?? '······'}</div>
        <div className={style(styles.pairingLabel)}>{t('dashboard-display-screen-waiting')}</div>
      </div>
    );
  }

  if (state.phase === 'paused') {
    return (
      <div className={style(styles.screen)}>
        <div className={style(styles.pairingLabel)}>{t('dashboard-display-screen-paused-message')}</div>
      </div>
    );
  }

  const { config, data } = state;
  const views = config.views ?? [];

  return (
    <div className={style(styles.screen)} onClick={registerInteraction} role="presentation">
      <div className={style(styles.viewLabel)}>
        {config.showDisplayName && config.name ? `${config.officeName} · ${config.name}` : config.officeName}
      </div>

      {/* Scrollable region: content that's taller than the screen (e.g. a long room list) scrolls
          in here instead of pushing the page taller, so the tab bar below never ends up
          overlapping mid-scroll content the way a position:fixed bar would. */}
      <div className={style(styles.content)}>
        {activeView === 'officeTotal' && data && (
          <>
            {/* The dial fills this wrapper rather than rendering at a fixed 260px, so it reads
                from across a lobby on a 4K wall and still fits a watch-sized screen. `size` is
                only the SVG's internal coordinate space now - the wrapper decides the pixels. */}
            <div className={style(styles.gaugeWrap)}>
              <Gauge
                value={data.office.occupancyPercentage}
                thresholdMin={data.office.thresholdMinPercentage}
                thresholdMax={data.office.thresholdMaxPercentage}
                size={260}
                onDarkBackground
                responsive
              />
            </div>
            <div className={style(styles.statRow)}>
              <div className={style(styles.stat)}>
                <div className={style(styles.statValue)}>{data.office.reserved}</div>
                <div className={style(styles.statLabel)}>{t('dashboard-kpi-reserved')}</div>
              </div>
              <div className={style(styles.stat)}>
                <div className={style(styles.statValue)}>{data.office.checkedIn}</div>
                <div className={style(styles.statLabel)}>{t('dashboard-kpi-checked-in')}</div>
              </div>
              <div className={style(styles.stat)}>
                <div className={style(styles.statValue)}>{data.office.effectiveCapacity}</div>
                <div className={style(styles.statLabel)}>{t('dashboard-kpi-capacity')}</div>
              </div>
            </div>
          </>
        )}

        {activeView === 'rooms' && data && (
          <div className={style(styles.tableWrap)}>
            <table className={style(styles.table)}>
              <thead>
                <tr>
                  <th className={style(styles.th('left'))}>{t('reporting-table-room')}</th>
                  <th className={style(styles.th('center'))}>{t('dashboard-table-morning')}</th>
                  <th className={style(styles.th('center'))}>{t('dashboard-table-afternoon')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Defaulted, not asserted: the kiosk is unattended, and a payload without this
                    collection (an older API, a trimmed public response, a partial deserialisation)
                    used to throw inside render and unmount the whole screen - a permanently blank
                    wall display that only a manual reload recovered. An empty table is a far better
                    failure mode, and the next 30s poll fills it in. */}
                {(data.rooms ?? []).map((room) => (
                  <tr key={room.roomId}>
                    <td className={style(styles.td('left'))}>{room.name}</td>
                    <td className={style(styles.td('center'))}>{room.morning.occupancyPercentage}%</td>
                    <td className={style(styles.td('center'))}>{room.afternoon.occupancyPercentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeView === 'parking' && data && (
          <div className={style(styles.tableWrap)}>
            <table className={style(styles.table)}>
              <thead>
                <tr>
                  <th className={style(styles.th('left'))}>{t('dashboard-display-view-parking')}</th>
                  <th className={style(styles.th('center'))}>{t('dashboard-table-morning')}</th>
                  <th className={style(styles.th('center'))}>{t('dashboard-table-afternoon')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.parkings ?? []).map((parking) => (
                  <tr key={parking.parkingId}>
                    <td className={style(styles.td('left'))}>{parking.name}</td>
                    <td className={style(styles.td('center'))}>{parking.morning.occupancyPercentage}%</td>
                    <td className={style(styles.td('center'))}>{parking.afternoon.occupancyPercentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeView === 'lunchCount' && data && (
          <div className={style(styles.lunchStack)}>
            <img src={burgerIcon} alt={t('lunch') ?? ''} className={style(styles.lunchIcon)} />
            <div className={style(styles.bigNumber)}>{data.lunchCount}</div>
          </div>
        )}
      </div>

      {config.mode === 0 && (
        <div className={style(styles.rotateDots)}>
          {views.map((view) => (
            <span key={view} className={style(styles.rotateDot(view === activeView))} />
          ))}
        </div>
      )}

      {config.mode === 1 && (
        <div className={style(styles.touchTabs)}>
          {views.map((view) => (
            <div
              key={view}
              role="button"
              tabIndex={0}
              className={style(styles.touchTab(view === activeView))}
              onClick={(): void => {
                setActiveView(view);
                registerInteraction();
              }}
              onKeyDown={(event): void => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setActiveView(view);
                  registerInteraction();
                }
              }}
            >
              {t(`dashboard-display-view-${view}`)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DisplayScreen;
